import 'Assets/styles/default/pages/concierge/NewConciergeSolicitationStyles.less';

import {
    notification
} from "@vertem-core/vertice-ds";

import PropTypes from 'prop-types';

import React, { useEffect, useRef, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { FaExclamationTriangle } from "react-icons/fa";

import { Col, Row } from 'antd';
import { Form, Typography, VertButton, VertInput } from '@vertem-core/vertice-ds';
import { NumericFormat } from 'react-number-format';

import { pointsMask } from 'Utils/commons/points';
import generateUUID from 'Utils/generateUUID';

import { getConvertedPriceThunk, resetConvertedPriceData } from 'redux/Reducers/Concierge/getConvertedPriceSlice';
import { getConvertedPointsThunk, resetConvertedPointsData } from 'redux/Reducers/Concierge/getConvertedPointsSlice';

import { currencyStringToFloat } from 'Utils/commons/string';
import { shouldConvertToPoints } from 'Constants/Concierge';

import { SolicitationItemInitialState } from 'Constants/Concierge/initialStateValues';

const CustomVertInput = ({ ...props }) => {
    const { field, label, disabled, className, handleChange, onChange } = props;
    const { name, customLabel } = field;

    const isDisabled = field.disabled || disabled;

    props = {
        ...props,
        name,
        label: customLabel || label,
        disabled: isDisabled,
        className: `${className} ${isDisabled ? 'field-disabled' : ''}`
    };

    const applyChange = ({ target }) => {
        onChange(target.value);
        if (handleChange) handleChange(target.value);
    }

    return <VertInput {...props} onChange={applyChange} />;
};

const CustomNumericInput = ({ ...props }) => {
    const { field, label, disabled, className, value, decimalScale, handleChange, onChange } = props;
    const { name, customLabel } = field;

    const [isFocused, setIsFocused] = useState(!!value);
    const isDisabled = field.disabled || disabled;

    const thousandSeparator = decimalScale > 0 ? "." : false;
    const decimalSeparator = decimalScale > 0 ? "," : undefined;

    props = {
        ...props,
        name,
        label: customLabel || label,
        disabled: isDisabled,
        className: `${className} ${isDisabled ? 'field-disabled' : ''}`
    };

    const applyChange = ({ value }) => {
        setIsFocused(!!value);
        onChange(value);
        if (handleChange) handleChange(value);
    }

    return (
        <div className={`vert-input-container ${isFocused ? "focused" : ""}`}>
            <label className="vert-input-label">{field?.customLabel || label}</label>
            <NumericFormat
                {...props}
                disabled={isDisabled}
                className="ant-input ant-input-outlined vert-input vert-shape-default vert-template-marketplace ant-input-size-default padding-x-undefined"
                displayType={"input"}
                thousandSeparator={thousandSeparator}
                decimalSeparator={decimalSeparator}
                fixedDecimalScale={true}
                allowNegative={false}
                isNumericString={true}
                decimalScale={decimalScale}
                onFocus={() => setIsFocused(true)}
                onValueChange={applyChange}
            />
        </div>
    );
};

const FormInformationProduct = ({ ...props }) => {

    const {
        solicitation,
        setSolicitation,
        userPoints,
        setShowForm,
        form,
        currentProductService,
        setCurrentProductService,
        isEditingProductService,
        setIsEditingProductService,
        editingProductServiceTotalPoints,
        handlePreviousStep,
        subcategoryFields,
        subcategoryDisclaimers
    } = props;

    const dispatch = useDispatch();
    const Catalog = useSelector(({ Catalog: data }) => data);
    const { currencyConfiguration: { decimalPlaces, pointsName } } = Catalog;
    const [loadingConversion, setLoadingConversion] = useState(false);

    const {
        convertedPrice,
        isConvertedPriceLoading
    } = useSelector(
        (state) => state.GetConvertedPrice
    );

    const {
        convertedPoints,
        isConvertedPointsLoading
    } = useSelector(
        (state) => state.GetConvertedPoints
    );

    const [timer, setTimer] = useState(null);

    const handleDescriptionChange = (value) => {
        clearTimeout(timer);
        const newTimer = setTimeout(() => {
            setCurrentProductService((prevState) => ({
                ...prevState,
                description: value
            }));
        }, 300);
        setTimer(newTimer);
    }

    const sumTotalPointsValue = () => {
        return solicitation.productList?.filter(a => a.pointsValue)
            .map(a => (a.pointsValue * a.quantity))
            .reduce((total, number) => total + number, 0);
    }

    const remainingPoints = () => {
        var editedTotalPoints = parseInt(isEditingProductService ? editingProductServiceTotalPoints : "0");
        return (userPoints.pointsValue - sumTotalPointsValue()) + editedTotalPoints;
    }

    const resetForm = () => {
        dispatch(resetConvertedPriceData());
        dispatch(resetConvertedPointsData());

        form.resetFields();
        setCurrentProductService(SolicitationItemInitialState);
        setIsEditingProductService(false);
    }

    const calculateConvertedPrice = () => {
        const fieldPoints = getField('pointsValue');
        if (fieldPoints?.rules?.includes('convertPointsToUnitValue')) return;

        const unitValue = form.getFieldValue('unitValue');
        const quantity = form.getFieldValue('quantity');

        if (!unitValue || !quantity) {
            form.setFieldValue('pointsValue', '0');
            form.setFieldValue('pointsTotalValue', '0');
            return;
        }

        const value = Number(removeMaskMoney(unitValue));

        requestConversion('unitValue', value, getConvertedPriceThunk);
    }

    const calculateConvertedPoints = () => {
        const field = getField('pointsValue');

        if (!field?.rules?.includes('convertPointsToUnitValue')) return;

        const pointsValue = form.getFieldValue('pointsValue');

        if (!pointsValue) {
            form.setFieldValue('unitValue', '0');
            form.setFieldValue('pointsTotalValue', '0');
            return;
        }

        requestConversion('pointsValue', pointsValue, getConvertedPointsThunk);
    }

    const debounceConversion = useRef(null);

    const requestConversion = (name, value, request) => {
        setLoadingConversion(true);

        if (debounceConversion.current) {
            clearTimeout(debounceConversion.current);
        }

        debounceConversion.current = setTimeout(() => {
            form.validateFields([name])
                .then(dispatch(request(value)))
                .catch((err) => {
                    const { errorFields } = err;
                    if (errorFields.length === 0) {
                        dispatch(request(value));
                    } else {
                        setLoadingConversion(false);
                    }
                });
        }, 1500);
    }

    const removeMaskMoney = (value) => {
        return value.replace('R$', '').replace(',', '.');
    }

    useEffect(() => {
        if (!convertedPrice) return;

        setLoadingConversion(false);

        const qtd = form.getFieldValue("quantity");
        const totalValue = convertedPrice.totalValuePoints * Number(qtd);
        const totalValueFormatted = totalValue
            .toLocaleString("pt-BR", { minimumFractionDigits: decimalPlaces, maximumFractionDigits: decimalPlaces });

        if (!shouldConvertToPoints) {
            setCurrentProductService({
                ...currentProductService,
                pointsValue: convertedPrice.totalValuePoints,
                pointsTotalValue: totalValueFormatted
            });
            validateRemainingPoints(totalValue);
            return;
        }

        form.setFieldValue('pointsValue', convertedPrice.formattedTotalValuePoints);
        form.setFieldValue('pointsTotalValue', totalValueFormatted);

        form.validateFields(["pointsValue"]);
        form.validateFields(["pointsTotalValue"]);

    }, [convertedPrice]);

    useEffect(() => {
        if (!convertedPoints) return;

        const value = convertedPoints.totalValueCurrency.toString();

        form.setFieldValue('unitValue', value);
        form.validateFields(["unitValue"]);

        setLoadingConversion(false);

    }, [convertedPoints]);

    useEffect(() => {
        setLoadingConversion(isConvertedPriceLoading || isConvertedPointsLoading);
    }, [isConvertedPriceLoading, isConvertedPointsLoading]);

    const rulesValidation = {
        'geaterThanBalance': {
            validate: (value, balance) => {
                return Number(value) > Number(balance);
            },
            message: 'Saldo em pontos não suficiente.'
        },
    }

    const validateRules = (value, rules) => {
        const errors = [];

        rules?.map(rule => {
            const validation = rulesValidation[rule];

            if (validation) {
                switch (rule) {
                    case 'geaterThanBalance':
                        if (validation.validate(value, userPoints.pointsValue)) {
                            errors.push(validation.message);
                        }
                        break;
                }
            }
        });

        return errors;
    }

    const validateFieldPointsValue = (value) => {
        if (!value) return Promise.reject();

        const pointsValue = currencyStringToFloat(value);
        form.setFieldValue('pointsTotalValue', pointsValue);

        const errors = [];

        if (Number(value) < 1) errors.push("Deve ser maior que 0.");

        const fieldRules = getField('pointsValue')?.rules;
        const validate = validateRules(pointsValue, fieldRules);

        if (validate.length > 0) errors.push(...validate);

        if (errors.length > 0) return Promise.reject(errors);
        else return Promise.resolve();
    }

    const validateFieldTotalPoints = (value) => {
        if (!value) return Promise.reject();

        const pointsTotalValue = currencyStringToFloat(value);

        if (pointsTotalValue > remainingPoints()) {
            return Promise.reject(new Error(`Saldo em ${pointsName} insuficiente.`));
        }

        return Promise.resolve();
    };

    const validateRemainingPoints = (totalValuePoints) => {
        const errors = totalValuePoints > remainingPoints()
            ? ["Saldo poderá não ser suficiente."]
            : [];

        form.setFields([
            {
                name: 'unitValue',
                errors,
            }
        ]);

        return errors.length === 0;
    }

    const validateLessThanOrEqualZero = (unitValue) => {
        if (!unitValue) return Promise.reject();

        const errors = [];

        if (Number(unitValue) < 0) errors.push("Deve ser maior ou igual a 0.");

        if (errors.length > 0) return Promise.reject(errors);
        else return Promise.resolve();
    }

    const validateProductQuantity = (quantity) => {
        if (!quantity) return Promise.reject();

        const errors = [];

        if (Number(quantity) < 1) errors.push("Deve ser maior que 0.");
        if (Number(quantity) % 1 !== 0) errors.push("Deve ser inteiro.");

        const fieldRules = getField('quantity')?.rules;

        const validate = validateRules(quantity, fieldRules);

        if (validate.length > 0) errors.push(...validate);

        if (errors.length > 0) return Promise.reject(errors);
        else return Promise.resolve();
    }

    const wrapperPoints = (points) => {
        return pointsMask(points, decimalPlaces, pointsName);
    }

    const customerBalance = wrapperPoints(userPoints.pointsValue);

    const addProductService = () => {
        form.validateFields()
            .then(() => {
                let product = form.getFieldsValue();

                if (!hasField('title')) {
                    product.title = currentProductService.subCategoryLabel;
                }

                if (!hasField('quantity')) {
                    product.quantity = 1;
                }

                if (!hasField('unitValue')) {
                    product.unitValue = '0';
                }
                else {
                    product.unitValue = removeMaskMoney(product.unitValue);
                }

                product.categoryLabel = currentProductService.categoryLabel;
                product.subCategoryLabel = currentProductService.subCategoryLabel;

                if (hasField('pointsValue', true) || shouldConvertToPoints) {
                    product.pointsValue = currencyStringToFloat(product.pointsValue);

                    const totalPoints = product.pointsTotalValue
                        ? currencyStringToFloat(product.pointsTotalValue)
                        : (product.pointsValue * product.quantity);

                    product.pointsTotalValue = totalPoints;
                }
                else {
                    product.pointsValue = currentProductService.pointsValue || 0;
                    product.pointsTotalValue = currentProductService.pointsTotalValue || 0;
                }

                if (!isEditingProductService) {
                    product.id = generateUUID();

                    if (solicitation.productList == undefined) solicitation.productList = [];

                    setSolicitation((solicitation) => ({
                        ...solicitation,
                        productList: [...solicitation.productList, product]
                    }))

                    setShowForm(false);
                    resetForm();

                    notification.success({
                        message: "Produto/Serviço adicionado com sucesso.",
                    });

                    return;
                }

                const newEditedProductList = solicitation.productList;
                solicitation.productList = [];

                newEditedProductList.map(productService => {
                    setSolicitation((solicitation) => {
                        if (productService.id == currentProductService.id) {
                            product.id = currentProductService.id;

                            return {
                                ...solicitation,
                                productList: [...solicitation.productList, product]
                            }
                        }

                        return {
                            ...solicitation,
                            productList: [...solicitation.productList, productService]
                        };
                    })
                });

                setShowForm(false);
                resetForm();

                notification.success({
                    message: "Edição concluída com sucesso.",
                });
            })
            .catch(() => {
                notification.error({
                    message: "Preencha os campos corretamente."
                })
            })
    }

    const getField = (fieldName, requiredInList = false) => {
        if (!requiredInList && !subcategoryFields?.length) return {};

        const field = subcategoryFields?.find(item => item.field == fieldName);

        if (field) {
            const disabled = field.rules?.includes('disabled');
            return { ...field, disabled };
        }
    }

    const hasField = (fieldName, requiredInList = false) => {
        const field = getField(fieldName, requiredInList);
        return field != undefined;
    }

    const cleanFields = () => {
        form.resetFields([
            'title',
            'quantity',
            'description',
            'pointsValue',
            'unitValue',
            'pointsTotalValue'
        ]);

        setCurrentProductService((prevState) => ({
            ...prevState,
            description: ''
        }));
    }

    const hasDisclaimers = hasField('unitValue') || hasField('description') || hasField('pointValue') || subcategoryDisclaimers?.length;

    return (
        <>
            <Row>
                {hasDisclaimers &&
                    <>
                        <Col span={24} className='margin-top-small important-info'>
                            <Typography.Text className='text-subtitle'>
                                Informações importantes <FaExclamationTriangle />
                            </Typography.Text>
                        </Col>

                        <Col span={24} className='margin-top-small'>
                            <ol className='concierge-ol-infos'>
                                {(hasField('unitValue') && !getField('unitValue')?.customLabel) &&
                                    <li>
                                        <Typography.Text>
                                            O valor unitário será usado pelo time de cotação apenas como referência e não representa o valor final.
                                        </Typography.Text>
                                    </li>
                                }

                                {hasField('description') &&
                                    <li>
                                        <Typography.Text>
                                            No campo de descrição é importante ser específico para garantir que a cotação considere todas as características do produto/serviço.
                                        </Typography.Text>
                                    </li>
                                }

                                {subcategoryDisclaimers?.map((disclaimer) => (
                                    <li>
                                        <Typography.Text>
                                            {disclaimer}
                                        </Typography.Text>
                                    </li>
                                ))}
                            </ol>
                        </Col>
                    </>
                }

                <Col span={24} className='margin-top-small'>
                    <Typography.Text className='font-weight-500'>
                        {solicitation.isHierarchy && `Saldo do cliente: ${customerBalance.value} ${customerBalance.label}`}
                    </Typography.Text>
                </Col>
            </Row>

            <Row gutter={8} className='margin-top-small'>
                {hasField('title') &&
                    <Col span={18}>
                        <Form.Item
                            name="title"
                            validateTrigger={['onChange']}
                            rules={[
                                {
                                    required: true,
                                    message: 'Campo obrigatório.'
                                },
                            ]}
                        >
                            <CustomVertInput
                                type='text'
                                field={getField('title')}
                                label={`Nome do produto/serviço`}
                            />
                        </Form.Item>
                    </Col>
                }

                {hasField('quantity') &&
                    <Col xs={12} sm={12} md={6} lg={6} xl={6}>
                        <Form.Item
                            name="quantity"
                            validateTrigger={['onChange']}
                            rules={[
                                {
                                    required: true,
                                    message: 'Campo obrigatório.'
                                },
                                {
                                    validator: (_, val) => validateProductQuantity(val)
                                }
                            ]}
                        >
                            <CustomVertInput
                                type='number'
                                field={getField('quantity')}
                                label={`Quantidade`}
                                handleChange={calculateConvertedPrice}
                            />
                        </Form.Item>
                    </Col>
                }

                {(hasField('pointsValue', true) || shouldConvertToPoints) && (
                    <Col xs={12} sm={12} md={7} lg={7} xl={7}>
                        <Form.Item
                            name="pointsValue"
                            rules={[
                                {
                                    required: true,
                                    message: 'Campo obrigatório.'
                                },
                                {
                                    validator: (_, val) => validateFieldPointsValue(val)
                                }
                            ]}>
                            <CustomNumericInput
                                field={getField('pointsValue')}
                                label={`Valor unit. em ${pointsName}`}
                                decimalScale={decimalPlaces}
                                handleChange={calculateConvertedPoints}
                            />
                        </Form.Item>
                    </Col>)
                }

                {hasField('unitValue') &&
                    <Col xs={12} sm={12} md={7} lg={7} xl={7}>
                        <Form.Item
                            name="unitValue"
                            validateTrigger={['onChange']}
                            rules={[
                                {
                                    required: true,
                                    message: 'Campo obrigatório.'
                                },
                                {
                                    validator: (_, val) => validateLessThanOrEqualZero(val)
                                }
                            ]}>
                            <CustomNumericInput
                                field={getField('unitValue')}
                                label="Valor unitário"
                                prefix="R$ "
                                decimalScale={2}
                                handleChange={calculateConvertedPrice}
                            />
                        </Form.Item>
                    </Col>
                }

                {shouldConvertToPoints && (
                    <Col xs={12} sm={12} md={6} lg={6} xl={6}>
                        <Form.Item
                            name="pointsTotalValue"
                            rules={[
                                {
                                    required: true,
                                    message: "Campo obrigatório."
                                },
                                {
                                    validator: (_, val) => validateFieldTotalPoints(val),
                                }
                            ]}
                        >
                            <CustomNumericInput
                                field={getField('pointsTotalValue')}
                                label={`Total em ${pointsName}`}
                                decimalScale={decimalPlaces}
                            />
                        </Form.Item>
                    </Col>
                )}

                {hasField('description') &&
                    <Col span={24}>
                        <Col className='space-description'>
                            <Form.Item
                                name="description"
                            >
                                <VertInput.TextArea
                                    type='text'
                                    maxLength={500}
                                    className='text-area-description'
                                    placeholder={getField('description')?.customLabel || "Descrição do produto/serviço"}
                                    disabled={getField('description')?.disabled}
                                    onChange={value => handleDescriptionChange(value.target.value)}
                                />
                            </Form.Item>
                        </Col>

                        <Row justify={'end'}>
                            <Col>
                                <Typography.Text className='description-limit-label'>
                                    Limite de caracteres: {currentProductService.description?.length}/500
                                </Typography.Text>
                            </Col>
                        </Row>
                    </Col>
                }
            </Row >

            <Row gutter={[8, 8]} className='margin-top-small'>

                <Col xs={24} sm={24} md={12} lg={12} xl={12}>

                    {
                        solicitation.productList?.length > 0 ? (
                            <VertButton
                                type='default'
                                size="default"
                                htmlType="button"
                                className='button-concierge'
                                onClick={() => {
                                    resetForm();
                                    setShowForm(false);
                                }}
                            >
                                <Typography.Text className='span-button'>
                                    Cancelar
                                </Typography.Text>
                            </VertButton>
                        ) : (
                            <VertButton
                                type='default'
                                size="default"
                                htmlType="button"
                                className='button-concierge'
                                onClick={() => {
                                    resetForm();
                                    handlePreviousStep();
                                }}
                            >
                                <Typography.Text className='span-button'>
                                    Voltar
                                </Typography.Text>
                            </VertButton>
                        )
                    }

                </Col>

                <Col xs={24} sm={24} md={12} lg={12} xl={12}>

                    <Row justify={'end'} gutter={8}>

                        <Col>
                            <VertButton
                                type='default'
                                size="default"
                                className='margin-bottom-divider button-concierge'
                                htmlType="button"
                                onClick={cleanFields}
                            >
                                <Typography.Text className='span-button'>
                                    Limpar
                                </Typography.Text>
                            </VertButton>
                        </Col>
                        <Col>
                            <VertButton
                                className='button-continue button-concierge'
                                type='highlight'
                                size="default"
                                htmlType="button"
                                onClick={() => { addProductService() }}
                                disabled={loadingConversion}
                                loading={loadingConversion}
                            >
                                {isEditingProductService ?
                                    <Typography.Text className='span-button-continue'>
                                        Editar
                                    </Typography.Text> :
                                    <Typography.Text className='span-button-continue'>
                                        Adicionar
                                    </Typography.Text>
                                }
                            </VertButton>
                        </Col>
                    </Row>
                </Col>
            </Row>
        </>
    )
}

FormInformationProduct.propTypes = {
    solicitation: PropTypes.object.isRequired,
    setShowForm: PropTypes.func.isRequired,
    form: PropTypes.object.isRequired,
    formFields: PropTypes.array.isRequired,
    currentProductService: PropTypes.object.isRequired,
    setCurrentProductService: PropTypes.func.isRequired,
    isEditingProductService: PropTypes.bool.isRequired,
    setIsEditingProductService: PropTypes.func.isRequired,
    editingProductServiceTotalPoints: PropTypes.number.isRequired,
    userPoints: PropTypes.object.isRequired,
    handlePreviousStep: PropTypes.func.isRequired,
    subcategoryFields: PropTypes.array.isRequired,
    subcategoryDisclaimers: PropTypes.array.isRequired
};

export default FormInformationProduct;
